import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/Button';
import { LabeledInput, LabeledInputStatus } from '@/shared/ui/LabeledInput';

type Props = {
  code: string;
  setCode: React.Dispatch<React.SetStateAction<string>>;
  maskedEmail: string;
  status: LabeledInputStatus;
  message: string;
  submit: () => void;
  resend: () => Promise<boolean>;
  back: () => void;
  isSubmitting: boolean;
  isResending: boolean;
};

const EmailMfaVerification = ({
  code,
  setCode,
  maskedEmail,
  status,
  message,
  submit,
  resend,
  back,
  isSubmitting,
  isResending,
}: Props) => {
  const t = useTranslations('Auth');
  const [resendCooldown, setResendCooldown] = useState(60);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(
      () => setResendCooldown((seconds) => seconds - 1),
      1000
    );
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending || isSubmitting) return;
    if (await resend()) setResendCooldown(60);
  };

  return (
    <div className="w-full">
      <p className="text-body2 mb-[24px] text-center leading-6 text-gray-500">
        {t('인증번호 안내', { email: maskedEmail })}
      </p>
      <LabeledInput
        id="mfa-code"
        label={t('인증번호')}
        placeholder={t('6자리 인증번호를 입력해 주세요')}
        value={code}
        onChangeValue={setCode}
        status={status}
        msg={message}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !isSubmitting && !isResending) submit();
        }}
      />
      <Button
        className="main:mt-[50px] main:mb-[24px] mt-[22px] mb-[16px]"
        onClick={submit}
        disabled={isSubmitting || isResending}
      >
        {isSubmitting ? t('인증 중') : t('인증하고 로그인')}
      </Button>
      <button
        type="button"
        className="text-primary mx-auto mb-[16px] block disabled:cursor-not-allowed disabled:text-gray-400"
        onClick={handleResend}
        disabled={resendCooldown > 0 || isResending || isSubmitting}
      >
        {isResending
          ? t('재전송 중')
          : resendCooldown > 0
            ? t('인증번호 재전송 대기', { seconds: resendCooldown })
            : t('인증번호 재전송')}
      </button>
      <button
        type="button"
        className="mx-auto block text-gray-400 underline"
        onClick={back}
        disabled={isSubmitting || isResending}
      >
        {t('이메일과 비밀번호 다시 입력')}
      </button>
    </div>
  );
};

export default EmailMfaVerification;
